import { type Control, type FieldValues } from 'react-hook-form';

/**
 * Emette un singolo ciclo di broadcast aggregato sullo state subject di RHF.
 *
 * Replica i 2 broadcast che RHF emetterebbe per ogni setValue/unregister
 * (uno per values, uno per formState), ma una sola volta per l'intero batch.
 * Il callback di _subscribe (index.esm.mjs:2014-2015) ri-legge _formValues
 * e _formState dalla closure, quindi riceve lo stato già aggiornato.
 * React 18 batcherà i 2 setState risultanti in un singolo re-render.
 */
export function emitAggregatedBroadcast(control: Control<FieldValues>) {
  // Shallow spread come fa _subscribe di RHF (index.esm.mjs:2014).
  control._subjects.state.next({ values: { ...control._formValues } });
  control._subjects.state.next({ ...control._formState });
}

/**
 * Esegue `fn` con i broadcast di `_subjects.state.next` soppressi,
 * poi emette un singolo broadcast aggregato nel finally.
 *
 * Ogni `setValue` di RHF causa `cloneObject(_formValues)` + broadcast
 * a tutti i subscriber. Con N chiamate → N × (clone ~2ms + broadcast) =
 * costo devastante. Questa utility sopprime i broadcast individuali e
 * ne emette uno solo alla fine con lo stato già aggiornato.
 *
 * Usata da `batchedUnregister` (batch di unregister per campi nascosti).
 */
export function withSuppressedBroadcast(
  control: Control<FieldValues>,
  fn: () => void,
): void {
  const stateSubject = control._subjects.state;
  // Guard: verifica che la struttura interna di RHF sia quella attesa.
  // Se `next` non è una funzione, fallback senza patch.
  if (typeof stateSubject.next !== 'function') {
    fn();
    return;
  }

  const originalNext = stateSubject.next;
  stateSubject.next = () => {};

  try {
    fn();
  } finally {
    stateSubject.next = originalNext;
    emitAggregatedBroadcast(control);
  }
}
